import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { CREATE_SERVING_CONTAINER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ServingContainer } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateServingContainerDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<ServingContainer.CreateRequest>({
        resolver: yupResolver(CREATE_SERVING_CONTAINER_SCHEMA),
        defaultValues: {},
    });
    const mutateCreateServingContainer = useMutation({
        mutationKey: ['servingContainer'],
        mutationFn: (data: ServingContainer.CreateRequest) =>
            Api.Servings.ServingContainer.postServingContainer(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: ServingContainer.CreateRequest) => {
        return mutateCreateServingContainer
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating serving container!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Serving Container
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        fullWidth
                        name="volumeOz"
                        label="Volume (oz)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 1000,
                                step: 0.1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="heightMm"
                        label="Height (mm)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 300,
                                step: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="diameterMm"
                        label="Diameter (mm)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 300,
                                step: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="widthMm"
                        label="Width (mm)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 300,
                                step: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        fullWidth
                        name="depthMm"
                        label="Depth (mm)"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 300,
                                step: 1,
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
